import { cssInterop } from 'nativewind';
import { memo, useCallback } from 'react';
import Linkify from 'react-native-linkify';

import { ActionBubble } from '@/components/chat/message-list/ActionBubble';
import { Box } from '@/components/ui/box';
import { Text } from '@/components/ui/text';
import { useMessageLinkOpener } from '@/hooks/chat/useMessageLinkOpener';

const StyledLinkify = cssInterop(Linkify, {
  linkClassName: 'linkStyle',
});

type PrimumSupportMessageBubbleProps = {
  text: string;
  accessibilityLabel?: string;
  alignEnd?: boolean;
  /** Shows the "Only you" badge, for a support message addressed to one participant. */
  showVisibilityBadge?: boolean;
  conversationId?: string;
  messageSid?: string;
  /** Report opened links as references, for expert content in this bubble. */
  linksAreReferences?: boolean;
};

export const PrimumSupportMessageBubble = memo(function PrimumSupportMessageBubble({
  text,
  accessibilityLabel = 'Primum Support message',
  alignEnd,
  showVisibilityBadge,
  conversationId,
  messageSid,
  linksAreReferences = false,
}: PrimumSupportMessageBubbleProps) {
  const openLink = useMessageLinkOpener({
    conversationId,
    messageSid,
    isReference: linksAreReferences,
  });

  /**
   * Linkify rewrites a bare `www.` link to `http://`. Opening the text as
   * written lets the shared opener give it `https://`, matching a link in a
   * regular message bubble.
   */
  const openSupportLink = useCallback(
    (url: string, linkText: string) => openLink(/^www\./i.test(linkText) ? linkText : url),
    [openLink]
  );

  return (
    <ActionBubble
      accessibilityLabel={accessibilityLabel}
      shrinkToContent
      alignEnd={alignEnd}
      showVisibilityBadge={showVisibilityBadge}>
      <Box className="min-w-0 flex-shrink">
        <StyledLinkify linkClassName="underline" onPress={openSupportLink}>
          <Text size="sm" className="text-typography-700">
            {text}
          </Text>
        </StyledLinkify>
      </Box>
    </ActionBubble>
  );
});
